import { defineMessages } from 'react-intl';
import { Outlet, generatePath } from 'react-router';

import { Cryptocurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CryptoIcon } from '@sorare/core/src/atoms/icons/CryptoIcon';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { iconCreditCard } from '@sorare/core/src/atoms/icons/system/iconCreditCard.ts';
import { iconWallet } from '@sorare/core/src/atoms/icons/system/iconWallet.ts';
import { TabContainer } from '@sorare/core/src/atoms/layout/TabContainer';
import {
  MY_SORARE_TRANSACTIONS_CASH,
  MY_SORARE_TRANSACTIONS_CREDITCARD,
  MY_SORARE_TRANSACTIONS_ESSENCE,
  MY_SORARE_TRANSACTIONS_ETH,
  MY_SORARE_TRANSACTIONS_SOL,
  MY_SORARE_TRANSACTIONS_VIP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { MyPage } from '#src/components/mySorare/MyPage/index.tsx';
import { MySorareSubTabsLayout } from '#src/components/mySorare/MySorareSubTabsLayout/index.tsx';

import { TRANSACTION_HISTORY_CONTENT_QUERY } from './query.ts';

const messages = defineMessages({
  FIAT: {
    defaultMessage: 'Cash Wallet',
  },
  ETH: {
    defaultMessage: 'ETH Wallet',
  },
  CREDIT_CARD: {
    defaultMessage: 'Credit card',
  },
  VIP: {
    defaultMessage: 'VIP',
  },
  ESSENCE: {
    defaultMessage: 'Essence',
  },
  SOL: {
    defaultMessage: 'SOL Wallet',
  },
});

export const TransactionsHistory = () => {
  const {
    currentUser,
    walletPreferences: { showFiatWallet, showCryptoWallet },
  } = useCurrentUserContext();
  const hasSolana = currentUser?.wallet?.solanaAddress;

  const { data } = useQuery(TRANSACTION_HISTORY_CONTENT_QUERY);

  if (!data?.currentUser) return null;

  const { highScarcityStatus } = data.currentUser;
  const showHighScarcityStatus =
    highScarcityStatus?.current?.status ||
    highScarcityStatus?.previous?.status ||
    (highScarcityStatus?.spendLeftToNextStatus?.eurCents &&
      highScarcityStatus.spendLeftToNextStatus.eurCents < 7500);

  const subTabs = [
    ...(showCryptoWallet && hasSolana
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_SOL),
            label: messages.SOL,
            icon: <CryptoIcon cryptocurrency={Cryptocurrency.LAMPORT} />,
          },
        ]
      : []),
    ...(showCryptoWallet
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_ETH),
            label: messages.ETH,
            icon: <CryptoIcon cryptocurrency={Cryptocurrency.WEI} />,
          },
        ]
      : []),
    ...(showFiatWallet
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_CASH),
            label: messages.FIAT,
            icon: <FontAwesomeIcon icon={iconWallet} />,
          },
        ]
      : []),
    {
      to: generatePath(MY_SORARE_TRANSACTIONS_CREDITCARD),
      label: messages.CREDIT_CARD,
      icon: <FontAwesomeIcon icon={iconCreditCard} />,
    },
    ...(showHighScarcityStatus
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_VIP),
            label: messages.VIP,
          },
        ]
      : []),
    {
      to: generatePath(MY_SORARE_TRANSACTIONS_ESSENCE),
      label: messages.ESSENCE,
      icon: <Shards size={2} />,
    },
  ];

  return (
    <MySorareSubTabsLayout tabs={subTabs}>
      <MyPage>
        <TabContainer>
          <Outlet />
        </TabContainer>
      </MyPage>
    </MySorareSubTabsLayout>
  );
};
